import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { getToken, URL } from "../../utils";
import axios from "axios";
import Select from "react-select";


// Construit un message lisible à partir des erreurs renvoyées par l'API (DRF).
const formatApiErrors = (data) => {
  if (!data) return "Une erreur est survenue.";
  if (typeof data === "string") return data;
  if (data.detail) return data.detail;
  try {
    const msgs = Object.entries(data).map(([field, val]) => {
      const text = Array.isArray(val) ? val.join(", ") : String(val);
      return `${field} : ${text}`;
    });
    return msgs.length ? msgs.join(" | ") : "Erreur de validation.";
  } catch {
    return "Erreur de validation. Veuillez vérifier les champs.";
  }
};

export default function ClientEditForm() {
  const [formData, setFormData] = useState({
    nom: "",
    prenom: "",
    contact1: "",
    contact2: "",
    adresse: "",
    date_naissance: "",
    email: null,
    profession: "",
    is_father: false,
    is_mother: false,
    is_grand_father: false,
    is_grand_mother: false,
    nationalite: "TG",
    sexe: "M",
    magasin: null,
    commercial: null,
    at_event: false,
    name_event: "",
  });
  const [validationErrors, setValidationErrors] = useState({});
  const [loading, setLoading] = useState(true);
  const [magasinOptions, setMagasinOptions] = useState([]);
  const [commercialOptions, setCommercialOptions] = useState([]);
  const [countries, setCountries] = useState([{ value: "TG", label: "Togo" }]);
  const [originalCountryName, setOriginalCountryName] = useState("");
  const { id } = useParams();
  const navigate = useNavigate();
  const token = getToken();

  // Fonction utilitaire pour mapper le nom du pays vers son code
  const mapCountryNameToCode = useCallback((countryName, countriesList) => {
    if (!countryName || !countriesList || countriesList.length === 0) {
      return "TG"; // Valeur par défaut
    }

    const country = countriesList.find(
      (c) => c.label.toLowerCase() === countryName.toLowerCase()
    );

    return country ? country.value : "TG";
  }, []);

  // Charger les données du client au montage du composant
  useEffect(() => {
    const loadData = async () => {
      await Promise.all([
        fetchClient(),
        fetchMagasins(),
        fetchCommerciaux(),
        fetchCountries(),
      ]);
    };
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Mapper le nom du pays vers son code une fois que les pays sont chargés
  useEffect(() => {
    if (originalCountryName && countries.length > 1) {
      // > 1 car on a au moins "Togo" par défaut
      const countryCode = mapCountryNameToCode(originalCountryName, countries);
      setFormData((prev) => ({
        ...prev,
        nationalite: countryCode,
      }));
    }
  }, [originalCountryName, countries, mapCountryNameToCode]);

  // Récupérer les données du client
  const fetchClient = async () => {
    try {
      const response = await axios.get(
        `${URL}/Clients/detail-client-particulier/${id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      const client = response.data;
      setFormData({
        nom: client.nom || "",
        prenom: client.prenom || "",
        contact1: client.contact1 || "",
        contact2: client.contact2 || "",
        adresse: client.adresse || "",
        date_naissance: client.date_naissance || "",
        email: client.email || "",
        profession: client.profession || "",
        is_father: client.is_father || false,
        is_mother: client.is_mother || false,
        is_grand_father: client.is_grand_father || false,
        is_grand_mother: client.is_grand_mother || false,
        nationalite: "TG", // Valeur temporaire, sera mise à jour par le useEffect
        sexe: client.sexe || "M",
        magasin: client.magasin ? parseInt(client.magasin) : null,
        commercial: client.commercial ? parseInt(client.commercial) : null,
        at_event: client.at_event || false,
        name_event: client.name_event || "",
      });

      // Stocker le nom original du pays pour le mapping
      setOriginalCountryName(client.nationalite || "Togo");
      setLoading(false);
    } catch (error) {
      toast.error("Erreur lors du chargement des données du client.");
      setLoading(false);
    }
  };

  // Récupérer les magasins
  const fetchMagasins = async () => {
    try {
      const response = await axios.get(`${URL}/Magasins/magasins-list`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      const options = response.data.map((magasin) => ({
        value: magasin.id,
        label: `${magasin.nom} - ${magasin.quartier}`,
      }));
      setMagasinOptions(options);
    } catch (error) {
      console.error("Erreur lors du chargement des magasins:", error);
      toast.error("Erreur lors du chargement des magasins");
    }
  };

  // Récupérer les commerciaux
  const fetchCommerciaux = async () => {
    try {
      const response = await axios.get(
        `${URL}/Magasins/commercial/liste-commerciaux`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      const options = response.data.map((commercial) => ({
        value: commercial.id,
        label: `${commercial.nom} - ${commercial.prenom}`,
      }));
      setCommercialOptions(options);
    } catch (error) {
      console.error("Erreur lors du chargement des commerciaux:", error);
      toast.error("Erreur lors du chargement des commerciaux");
    }
  };

  // Récupérer les pays
  const fetchCountries = async () => {
    try {
      const response = await axios.get(`${URL}/Clients/countries-list`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setCountries(
        response.data.map((country) => ({
          value: country.code,
          label: country.name,
        }))
      );
    } catch (error) {
      console.error("Erreur lors du chargement des pays:", error);
      toast.error("Erreur lors du chargement des pays");
    }
  };

  // Gestionnaire de soumission du formulaire
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.put(
        `${URL}/Clients/edit-client-particulier/${id}`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      toast.success("Client mis à jour avec succès !");
      navigate(`/clients/particuliers/show/${id}`);
    } catch (error) {
      if (error.response && error.response.data) {
        setValidationErrors(error.response.data);
        toast.error(formatApiErrors(error.response.data));
      } else if (error.response) {
        toast.error("Erreur lors de la mise à jour du client.");
      } else {
        toast.error(
          error.message || "Erreur réseau. Vérifiez votre connexion."
        );
      }
    }
  };

  // Gestionnaire de changement des champs du formulaire
  const handleChange = (e) => {
    if (!e || !e.target) return;
    const { id, name, value, type, checked } = e.target;
    const fieldName = name || id; // Utiliser name pour les radios, id pour les autres
    setFormData((prev) => ({
      ...prev,
      [fieldName]: type === "checkbox" ? checked : value,
    }));
  };

  // Gestionnaire pour les types de client (checkboxes avec gestion cohérente)
  const handleTypeChange = (e) => {
    const { name, checked } = e.target;

    setFormData((prev) => {
      const newData = {
        ...prev,
        [name]: checked,
      };

      // Empêcher la sélection croisée entre genres
      if (checked) {
        if (name === "is_father" || name === "is_grand_father") {
          newData.is_mother = false;
          newData.is_grand_mother = false;
        }
        if (name === "is_mother" || name === "is_grand_mother") {
          newData.is_father = false;
          newData.is_grand_father = false;
        }
      }
      return newData;
    });
  };

  // Gestionnaire pour les selects
  const handleSelectChange = (selectedOption, field) => {
    setFormData((prev) => ({
      ...prev,
      [field]: selectedOption ? selectedOption.value : null,
    }));
  };

  // Style personnalisé pour les cases à cocher avec gestion cohérente
  const CheckboxField = ({ name, label, description = "" }) => (
    <div className="flex items-center mb-4">
      <input
        type="checkbox"
        id={name}
        name={name}
        checked={formData[name]}
        onChange={handleTypeChange}
        className="w-4 h-4 text-amber-600 bg-gray-100 border-gray-300 rounded focus:ring-amber-500 focus:ring-2"
      />
      <label htmlFor={name} className="ml-2 text-sm font-medium text-gray-900">
        {label}
        {description && <p className="text-xs text-gray-500">{description}</p>}
      </label>
      {validationErrors[name] && (
        <p className="ml-2 text-red-500 text-sm">{validationErrors[name]}</p>
      )}
    </div>
  );

  // Composant CheckboxField pour événement
  const CheckboxFieldEvent = ({ id, label, checked, onChange }) => (
    <div className="flex items-center gap-2">
      <input
        type="checkbox"
        id={id}
        className="form-checkbox text-amber-600 rounded-md h-5 w-5"
        checked={checked}
        onChange={onChange}
      />
      <label htmlFor={id} className="text-sm text-gray-900">
        {label}
      </label>
    </div>
  );

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="flex flex-col items-center">
          {/* Animation */}
          <div className="loader"></div>
          <div className="w-10 h-10 border-2 border-t-2 border-gray-200 rounded-full animate-spin my-4"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 w-[min(600px,90%)] mx-auto mt-4 bg-white shadow-md rounded-md">
      <ToastContainer />
      <div className="flex gap-4 p-4 pb-0">
        <div className="p-3 bg-amber-200 rounded-full text-amber-600 grid justify-center items-center">
          <svg
            className="w-8 h-8"
            aria-hidden="true"
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            fill="none"
            viewBox="0 0 24 24"
          >
            <path
              stroke="currentColor"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M16 12h4m-2 2v-4M4 18v-1a3 3 0 0 1 3-3h4a3 3 0 0 1 3 3v1a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1Zm8-10a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z"
            />
          </svg>
        </div>
        <div className="">
          <h1 className="text-lg font-semibold">Modifier le client</h1>
          <p className="text-sm text-gray-500">
            Modifiez les informations du client
          </p>
        </div>
      </div>
      <form className="p-4" onSubmit={handleSubmit}>
        {/* Champ Nom */}
        <div className="mb-4">
          <label
            htmlFor="nom"
            className="block mb-2 text-sm font-medium text-gray-900"
          >
            Nom <span className="text-red-500 text-xl font-bold pl-1">*</span>
          </label>
          <input
            type="text"
            id="nom"
            className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-amber-500 focus:border-amber-500 block w-full p-2.5"
            placeholder="Nom"
            value={formData.nom}
            onChange={handleChange}
            required
          />
          {validationErrors.nom && (
            <p className="text-red-500 text-sm">{validationErrors.nom}</p>
          )}
        </div>

        {/* Champ Prénom */}
        <div className="mb-4">
          <label
            htmlFor="prenom"
            className="block mb-2 text-sm font-medium text-gray-900"
          >
            Prénoms
          </label>
          <input
            type="text"
            id="prenom"
            className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-amber-500 focus:border-amber-500 block w-full p-2.5"
            placeholder="Prénoms"
            value={formData.prenom}
            onChange={handleChange}
          />
          {validationErrors.prenom && (
            <p className="text-red-500 text-sm">{validationErrors.prenom}</p>
          )}
        </div>

        {/* Champ Contact 1 et Contact 2 */}
        <div className="flex gap-4 mb-4 max-sm:flex-col">
          <div className="flex-grow sm:w-1/2">
            <label
              htmlFor="contact1"
              className="block mb-2 text-sm font-medium text-gray-900"
            >
              Contact 1{" "}
              <span className="text-red-500 text-xl font-bold">*</span>
            </label>
            <input
              type="tel"
              id="contact1"
              className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-amber-500 focus:border-amber-500 block w-full p-2.5"
              placeholder="Contact 1"
              value={formData.contact1}
              onChange={handleChange}
              required
            />
            {validationErrors.contact1 && (
              <p className="text-red-500 text-sm">
                {validationErrors.contact1}
              </p>
            )}
          </div>
          <div className="flex-grow sm:w-1/2">
            <label
              htmlFor="contact2"
              className="block mb-2 text-sm font-medium text-gray-900"
            >
              Contact 2
            </label>
            <input
              type="tel"
              id="contact2"
              className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-amber-500 focus:border-amber-500 block w-full p-2.5"
              placeholder="Contact 2"
              value={formData.contact2}
              onChange={handleChange}
            />
            {validationErrors.contact2 && (
              <p className="text-red-500 text-sm">
                {validationErrors.contact2}
              </p>
            )}
          </div>
        </div>

        {/* Nationalité */}
        <div className="mb-4">
          <label
            htmlFor="nationalite"
            className="block mb-2 text-sm font-medium text-gray-900"
          >
            Nationalité <span className="text-red-500">*</span>
          </label>
          <Select
            options={countries}
            value={
              countries.find(
                (country) => country.value === formData.nationalite
              ) || null
            }
            onChange={(selectedOption) =>
              handleSelectChange(selectedOption, "nationalite")
            }
            placeholder="Sélectionner une nationalité..."
            noOptionsMessage={() => "Aucun résultat"}
            isClearable
            styles={{
              control: (base) => ({
                ...base,
                borderColor: "#d1d5db",
                boxShadow: "none",
                "&:hover": {
                  borderColor: "#f59e0b",
                },
              }),
            }}
            required={true}
          />
          {validationErrors.nationalite && (
            <p className="text-red-500 text-sm">
              {validationErrors.nationalite}
            </p>
          )}
        </div>

        {/* Champ Adresse */}
        <div className="mb-4">
          <label
            htmlFor="adresse"
            className="block mb-2 text-sm font-medium text-gray-900"
          >
            Adresse
          </label>
          <input
            type="text"
            id="adresse"
            className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-amber-500 focus:border-amber-500 block w-full p-2.5"
            placeholder="Adresse"
            value={formData.adresse}
            onChange={handleChange}
          />
          {validationErrors.adresse && (
            <p className="text-red-500 text-sm">{validationErrors.adresse}</p>
          )}
        </div>

        {/* Champ Date de naissance */}
        <div className="mb-4">
          <label
            htmlFor="date_naissance"
            className="block mb-2 text-sm font-medium text-gray-900"
          >
            Date de naissance
          </label>
          <input
            type="date"
            id="date_naissance"
            className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-amber-500 focus:border-amber-500 block w-full p-2.5"
            value={formData.date_naissance}
            onChange={handleChange}
          />
          {validationErrors.date_naissance && (
            <p className="text-red-500 text-sm">
              {validationErrors.date_naissance}
            </p>
          )}
        </div>

        {/* Champ Email */}
        <div className="mb-4">
          <label
            htmlFor="email"
            className="block mb-2 text-sm font-medium text-gray-900"
          >
            Email
          </label>
          <input
            type="email"
            id="email"
            className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-amber-500 focus:border-amber-500 block w-full p-2.5"
            placeholder="Email"
            value={formData.email}
            onChange={handleChange}
          />
          {validationErrors.email && (
            <p className="text-red-500 text-sm">{validationErrors.email}</p>
          )}
        </div>

        {/* Champ Profession */}
        <div className="mb-4">
          <label
            htmlFor="profession"
            className="block mb-2 text-sm font-medium text-gray-900"
          >
            Profession{" "}
            <span className="text-red-500 text-xl font-bold pl-1">*</span>
          </label>
          <input
            type="text"
            id="profession"
            className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-amber-500 focus:border-amber-500 block w-full p-2.5"
            placeholder="Profession"
            value={formData.profession}
            onChange={handleChange}
          />
          {validationErrors.profession && (
            <p className="text-red-500 text-sm">
              {validationErrors.profession}
            </p>
          )}
        </div>

        {/* Magasin */}
        <div className="mb-4">
          <label className="block mb-2 text-sm font-medium text-gray-900">
            Magasin
          </label>
          <Select
            options={magasinOptions}
            value={magasinOptions.find((option) => {
              return option.value === formData.magasin;
            })}
            onChange={(selectedOption) =>
              handleSelectChange(selectedOption, "magasin")
            }
            placeholder="Sélectionner un magasin"
            isClearable
            className="text-sm"
            styles={{
              control: (provided) => ({
                ...provided,
                borderColor: "#d1d5db",
                "&:hover": {
                  borderColor: "#f59e0b",
                },
              }),
            }}
          />
          {validationErrors.magasin && (
            <p className="text-red-500 text-sm">{validationErrors.magasin}</p>
          )}
        </div>

        {/* Commercial */}
        <div className="mb-4">
          <label className="block mb-2 text-sm font-medium text-gray-900">
            Commercial
          </label>
          <Select
            options={commercialOptions}
            value={commercialOptions.find((option) => {
              return option.value === formData.commercial;
            })}
            onChange={(selectedOption) =>
              handleSelectChange(selectedOption, "commercial")
            }
            placeholder="Sélectionner un commercial"
            isClearable
            className="text-sm"
            styles={{
              control: (provided) => ({
                ...provided,
                borderColor: "#d1d5db",
                "&:hover": {
                  borderColor: "#f59e0b",
                },
              }),
            }}
          />
          {validationErrors.commercial && (
            <p className="text-red-500 text-sm">
              {validationErrors.commercial}
            </p>
          )}
        </div>

        {/* Sexe */}
        <div className="mb-4">
          <label className="block mb-2 text-sm font-medium text-gray-900">
            Sexe
          </label>
          <div className="flex items-center gap-4">
            <div className="flex items-center">
              <input
                type="radio"
                id="sexe_masculin"
                name="sexe"
                value="M"
                checked={formData.sexe === "M"}
                onChange={handleChange}
                className="w-4 h-4 text-amber-600 bg-gray-100 border-gray-300 focus:ring-amber-500 focus:ring-2"
              />
              <label
                htmlFor="sexe_masculin"
                className="ml-2 text-sm font-medium text-gray-900"
              >
                Masculin
              </label>
            </div>
            <div className="flex items-center">
              <input
                type="radio"
                id="sexe_feminin"
                name="sexe"
                value="F"
                checked={formData.sexe === "F"}
                onChange={handleChange}
                className="w-4 h-4 text-amber-600 bg-gray-100 border-gray-300 focus:ring-amber-500 focus:ring-2"
              />
              <label
                htmlFor="sexe_feminin"
                className="ml-2 text-sm font-medium text-gray-900"
              >
                Féminin
              </label>
            </div>
          </div>
          {validationErrors.sexe && (
            <p className="text-red-500 text-sm">{validationErrors.sexe}</p>
          )}
        </div>

        {/* Types de client */}
        <div className="mb-4 p-4 border border-gray-200 rounded-lg">
          <h3 className="mb-3 text-sm font-medium text-gray-900">
            Type de client
          </h3>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <CheckboxField
              name="is_father"
              label="Client père"
              description="Cocher si le client est un père"
            />
            <CheckboxField
              name="is_mother"
              label="Client mère"
              description="Cocher si le client est une mère"
            />
            <CheckboxField
              name="is_grand_father"
              label="Client grand-père"
              description="Cocher si le client est un grand-père"
            />
            <CheckboxField
              name="is_grand_mother"
              label="Client grand-mère"
              description="Cocher si le client est une grand-mère"
            />
          </div>
        </div>

        {/* Champ Événement */}
        <div className="mb-4">
          <CheckboxFieldEvent
            id="at_event"
            label="Présent à un événement"
            checked={formData.at_event}
            onChange={handleChange}
          />
        </div>

        {/* Nom de l'événement (conditionnel) */}
        {formData.at_event && (
          <div className="mb-4">
            <label
              htmlFor="name_event"
              className="block mb-2 text-sm font-medium text-gray-900"
            >
              Nom de l'événement
            </label>
            <input
              type="text"
              id="name_event"
              className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-amber-500 focus:border-amber-500 block w-full p-2.5"
              placeholder="Nom de l'événement"
              value={formData.name_event}
              onChange={handleChange}
            />
            {validationErrors.name_event && (
              <p className="text-red-500 text-sm">
                {validationErrors.name_event}
              </p>
            )}
          </div>
        )}

        {/* Boutons Annuler et Modifier */}
        <div className="grid grid-cols-2 items-center gap-4">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="hover:text-white border border-gray-800 bg-gray-200 hover:bg-gray-900 focus:outline-none focus:ring-4 focus:ring-gray-300 font-medium rounded-lg text-sm px-5 py-2.5 text-center"
          >
            Annuler
          </button>
          <button
            type="submit"
            className="text-white bg-amber-700 hover:bg-amber-800 focus:ring-4 focus:outline-none focus:ring-amber-300 font-medium rounded-lg text-sm w-full sm:w-auto px-5 py-2.5 text-center"
          >
            Modifier
          </button>
        </div>
      </form>
    </div>
  );
}
